import {
  GetAppDownloadBannerStateDocument,
  useGetAppDownloadBannerStateQuery,
  useGetUserAgentQuery,
} from '@kijiji/generated/graphql-types'
import SvgClose from '@kijiji/icons/src/icons/Close'
import { useTranslation } from 'next-i18next'
import React, { useState } from 'react'
import { useTheme } from 'styled-components'

import { APP_DOWNLOAD_COOKIE_KEY } from '@/components/shared/app-download-banner/constants'
import {
  CloseButton,
  Container,
  Content,
  Info,
  LearnMoreButton,
  Logo,
} from '@/components/shared/app-download-banner/styled'
import { CookieRegistry } from '@/constants/cookieRegistry'
import { useRunOnce } from '@/hooks/useRunOnce'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { BodyText } from '@/ui/atoms/body-text'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { createCookie } from '@/utils/cookies/createCookie'
import { getCookieByKey } from '@/utils/cookies/getCookieByKey'
import { getUserDevice } from '@/utils/getUserDevice'
import AppLogo from '~/public/next-assets/images/kijiji-app-logo-small.png'

/**
 * There is no easy way to detect if the user is using a third party browser on iOS.. I think.
 *
 * This approach checks if the user agent contains **iOS/** which is a good indicator that
 * the user is using a third party browser.
 *
 * e.g., CriOS/v1... or FxiOS/v1...
 */
export const isThirdPartyiOS = (ua?: string) => !!ua?.includes('iOS/')

/**
 * The AppDownloadBanner is used to promote the Kijiji app download
 * It is only shown on Android devices. iOS has its own app download banner.
 *
 * @see https://developer.apple.com/documentation/webkit/promoting-apps-with-smart-app-banners
 */
export const AppDownloadBanner = () => {
  const { t } = useTranslation('common')
  const { colors } = useTheme()
  const { data: userAgentData } = useGetUserAgentQuery()
  const userAgent = userAgentData?.userAgent.userAgent ?? undefined

  const { isAndroidDevice, isAppleDevice } = getUserDevice(userAgent)

  const { data: bannerState, client } = useGetAppDownloadBannerStateQuery({
    ssr: true,
  })

  // Switching between isVisible and hidden is confusing, I know..
  const [hidden, setHidden] = useState<boolean>(!bannerState?.appDownloadBanner.isVisible)

  useRunOnce(() => {
    if (!hidden) {
      // Track event is using PSA to match FES implementation. It's not a PSA banner.
      trackEvent({ action: GA_EVENT.PSALoaded, label: 'PSA=appDownloadPSA;' })
    }
  })

  const dismiss = () => {
    setHidden(true)
    trackEvent({ action: GA_EVENT.PSADismissed, label: 'PSA=appDownloadPSA;' })

    const existing = getCookieByKey(document.cookie, CookieRegistry.SESSION_DISMISS)
    const newVal = existing
      ? `${decodeURIComponent(existing)},${APP_DOWNLOAD_COOKIE_KEY}`
      : APP_DOWNLOAD_COOKIE_KEY

    // Session seems too frequent IMO, but this is how it was implemented in FES.
    createCookie(CookieRegistry.SESSION_DISMISS, encodeURIComponent(newVal))

    client.cache.writeQuery({
      query: GetAppDownloadBannerStateDocument,
      data: {
        appDownloadBanner: {
          ...bannerState?.appDownloadBanner, // don't need, just in-case
          isVisible: false,
        },
      },
    })
  }

  const trackButtonClick = () => {
    trackEvent({ action: GA_EVENT.PSAClick, label: 'PSA=appDownloadPSA;' })
  }

  // iOS with Safari has its own app download banner, so we don't need to show this one
  const shouldShow = isAndroidDevice || (isAppleDevice && isThirdPartyiOS(userAgent))
  if (hidden || !shouldShow) return null

  return (
    <Container data-testid="app-download-banner">
      <Content>
        <CloseButton aria-label={t('common:forms.buttons.close')} onClick={dismiss}>
          <SvgClose aria-hidden />
        </CloseButton>

        <Logo srcImage={AppLogo} alt={t('common:app_download_banner.logo.alt')} skipNextImage />

        <Info>
          <HeadlineText as="span" size="medium">
            {t('common:app_download_banner.title')}
          </HeadlineText>
          <BodyText as="span" size="small" color={colors.grey.primary}>
            {t('common:app_download_banner.description')}
          </BodyText>
        </Info>

        <LearnMoreButton
          as="a"
          href={
            isAppleDevice
              ? t('routes:external.app_store.href')
              : t('routes:external.google_play.href')
          }
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackButtonClick}
        >
          {t('common:app_download_banner.button.text')}
        </LearnMoreButton>
      </Content>
    </Container>
  )
}
